/**
 * The `Donut` catalog component: an allocation drawn as a ring.
 *
 * Measured off screen 7e of the mobile redesign — "Portfolio,
 * allocation-first: the shape before the money". A 120-unit viewBox, a ring
 * at r=46 with a 16-unit stroke, one arc per sector laid end to end by
 * `stroke-dasharray` against the circumference, and a legend beside it: a
 * swatch, the sector, and its percentage in tabular figures.
 *
 * A RING CLOSES AND A RAIL DOES NOT, which is why this exists beside
 * `allocation` rather than instead of it. `analyse_concentration` returns
 * `top_n` sectors, and five arcs joined into a circle say those five are the
 * book. That is a claim the rows do not make, so the catalog entry asserts
 * `distribution` and `may_draw` gets to refuse it where claims may not draw.
 * The rail keeps drawing there, because five bars beside a note are a
 * statement about five sectors and nothing more.
 *
 * THE RING IS DRAWN FROM THE SHARES AND NEVER NORMALISED. The arcs are laid
 * at their own lengths against the full circumference, so five sectors that
 * come to 88% leave the last twelve per cent of the ring empty — which is
 * the honest picture, and the one thing this shape can say that a rail
 * cannot. Scaling them to close the circle would be the client computing
 * percentages, and computing the exact percentages the shape's claim rests
 * on.
 *
 * Colour is identity, not judgement — `shapes/identity.ts` says the same of
 * the same eight hues. Which hue a sector gets says nothing about the sector;
 * it is there so the eye can carry one arc to its label without reading
 * either. The values live in `embed.css` with their contrast measurement, and
 * they are read here as CSS variables because an SVG stroke takes a colour
 * rather than a class.
 */
import { useState } from 'react'
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import { cellNumber, type CellValue } from '@/features/mitra/lib/figures'
import { Fig, Label } from '@/features/mitra/panel/blocks/Card'
import { assigner, type Slot } from '@/features/mitra/panel/blocks/shapes/identity'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const DonutApi = {
  name: 'Donut',
  schema: z
    .object({
      slices: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/slices`, fixed by `to_messages`. */
export type SlicePart = { id: string; label: string; share: CellValue }

/**
 * The ring's geometry, in the viewBox's own units. `C` is 2πr and is what
 * every arc length is a fraction of; it is computed rather than written out
 * so a change to the radius cannot leave it stale.
 */
const R = 46
const C = 2 * Math.PI * R

/**
 * What a slot looks like HERE, as CSS variables rather than classes.
 *
 * `identity.ts` decides WHICH slot a sector gets; this file only draws one.
 * Variables and not classes because an SVG stroke takes a colour, and written
 * out rather than assembled for the reason `identity.ts` writes its classes
 * out: a name built as `var(--color-series-${n})` is a name no stylesheet was
 * asked for, and its failure mode is a colourless arc rather than an error.
 *
 * FOUR, AND THEY ARE NOT THE FOUR THIS CHART HAD IN AUGUST. Those measured
 * two greys and a near-black; these are four hues that are four hues. Eight
 * were tried in between and separated on ADJACENT pairs only, which is the
 * wrong gate for a DONUT above all — its arcs are compared out of sequence,
 * so slot 2 beside slot 5 is the ordinary case here rather than the corner
 * one. `app/embed.css` has the search: four is the largest count that clears
 * every pair in both themes.
 *
 * KEYED BY `Slot`, NOT BY NUMBER. A fifth slot added to the ramp is a type
 * error in this file rather than an arc drawn in nothing.
 */
const SERIES_VAR: Record<Slot, string> = {
  1: 'var(--color-series-1)',
  2: 'var(--color-series-2)',
  3: 'var(--color-series-3)',
  4: 'var(--color-series-4)',
  // NOT A FIFTH HUE. `rest` means "not individually identified", which is a
  // true statement about the fifth of nine sectors where a repeated hue is a
  // false one, and it is the same statement the token already makes as the
  // quiet half of a subject/peer pair. A sixth and a seventh fold to it too
  // and are indistinguishable from each other — deliberately, because they
  // are indistinguishable in what the chart is claiming about them.
  rest: 'var(--color-series-rest)',
}

/**
 * A slice's weight, clamped to the ring before anything reads it.
 *
 * One reading, because the arcs and the hue assignment must agree about which
 * sectors are the large ones: a share the wire sent as 140 is 100 to both or
 * the ring and the legend are drawn from two different sets of figures.
 */
const weight = (slice: SlicePart) => Math.min(Math.max(cellNumber(slice.share), 0), 100)

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const donutApi = DonutApi as unknown as ComponentApi

export const Donut = createComponentImplementation<ComponentApi>(
  donutApi,
  ({ props }: { props: { slices?: unknown; caption?: unknown } }) => (
    <DonutRing
      slices={listOf<SlicePart>(props.slices)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/** The drawing, given the list the surface resolved. */
function DonutRing({ slices, caption }: { slices: SlicePart[]; caption: string | null }) {
  const [active, setActive] = useState<string | null>(null)
  if (slices.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }
  // ONE ASSIGNMENT PER RING, ON THE SECTOR'S OWN NAME, AND THE INDEX IS GONE.
  // It was `HUES[index % HUES.length]` and both halves of that were wrong.
  // The modulo handed the ninth sector the first one's colour, so two arcs on
  // one ring carried one identity — and on a donut the two are not even
  // adjacent, so the reader has no gap or ordering to tell them apart. The
  // index itself was the other half: the hue said "you are the largest",
  // so Hydropower slipping to second changed colour while the reader watched.
  //
  // THE RAIL DRAWS THESE SAME ROWS. `shapes/Allocation` renders the same
  // sectors and keys its hues on the same label, so a ring and a rail in one
  // answer would otherwise disagree about which colour Hydropower is — the
  // one thing a hue must never do. Handed in descending share, which is the
  // order `Allocation` sorts into before it calls: past the eighth sector
  // `identity.ts` folds to the tail, and the tail has to be the same sectors
  // on both drawings or each names a sector the other calls anonymous.
  const hueOf = assigner([...slices].sort((a, b) => weight(b) - weight(a)).map((s) => s.label))
  const colourOf = (slice: SlicePart) => SERIES_VAR[hueOf(slice.label)]
  // Clamped at the ring, not at each arc: a set of shares that sums past 100
  // is a tool bug, and an arc drawn twice round the circle would hide it
  // behind a picture that still looks right.
  const { arcs } = slices.reduce<{
    arcs: { key: string; length: number; offset: number; fill: string }[]
    offset: number
  }>(
    (previous, s) => {
      const share = weight(s)
      const length = (Math.min(share, Math.max(100 - previous.offset, 0)) / 100) * C
      const arc = {
        key: s.id,
        length,
        offset: (previous.offset / 100) * C,
        fill: colourOf(s),
      }
      return { arcs: [...previous.arcs, arc], offset: previous.offset + share }
    },
    { arcs: [], offset: 0 },
  )

  return (
    <figure
      data-role="chart"
      data-component="donut"
      data-has-active={active !== null ? 'true' : 'false'}
      className="agent-donut m-0 flex flex-col gap-3 px-3 pb-2 pt-3"
    >
      <div className="flex items-center justify-between gap-3">
        <Label className="text-ink-mid">Sector allocation</Label>
        <span className="text-mono-s text-ink-soft">{slices.length} sectors</span>
      </div>
      <div data-role="donut-layout" className="flex flex-wrap items-center gap-5">
        <svg
          viewBox="0 0 120 120"
          role="img"
          aria-label="Sector allocation chart"
          className="agent-donut-svg h-37 w-37 shrink-0"
        >
          {/* The unfilled remainder, drawn first so the arcs sit on it. It is
            what makes an incomplete ring legible as incomplete rather than
            as a ring with a gap in it. */}
          <circle cx="60" cy="60" r={R} fill="none" strokeWidth="16" stroke="var(--color-rule)" />
          {arcs.map((a) => (
            <circle
              key={a.key}
              data-role="arc"
              data-active={active === a.key ? 'true' : undefined}
              cx="60"
              cy="60"
              r={R}
              fill="none"
              strokeWidth="16"
              stroke={a.fill}
              strokeDasharray={`${String(a.length)} ${String(C)}`}
              strokeDashoffset={-a.offset}
              transform="rotate(-90 60 60)"
              onPointerEnter={(event) => {
                if (event.pointerType !== 'touch') setActive(a.key)
              }}
              onPointerLeave={(event) => {
                if (event.pointerType !== 'touch') setActive(null)
              }}
            />
          ))}
          {/* NOTHING IN THE MIDDLE, and 7e puts "2 / SECTORS CARRY IT" there.
            That sentence is a judgement — how many sectors count as carrying
            a book — reached by counting arcs, and a figure this component
            computed would be a figure no tool produced. The legend carries
            the substance; the hole stays empty. */}
        </svg>
        <ul className="agent-donut-legend m-0 flex min-w-38 flex-1 list-none flex-col gap-2 p-0">
          {slices.map((s) => (
            <li key={s.id} data-role="slice" data-active={active === s.id ? 'true' : undefined}>
              <button
                type="button"
                className="flex w-full items-start gap-2 rounded-lg text-left
                       focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-deep"
                onPointerEnter={(event) => {
                  if (event.pointerType !== 'touch') setActive(s.id)
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType !== 'touch') setActive(null)
                }}
                onFocus={() => {
                  setActive(s.id)
                }}
                onBlur={() => {
                  setActive(null)
                }}
                onClick={() => {
                  setActive(s.id)
                }}
              >
                <span
                  data-role="slice-swatch"
                  aria-hidden="true"
                  className="mt-1 block h-2 w-2 shrink-0 rounded-full"
                  style={{ background: colourOf(s) }}
                />
                <span className="min-w-0 flex-1 break-words text-body-s leading-snug text-ink-mid">
                  {s.label}
                </span>
                <Fig cell={s.share} className="shrink-0 text-body-s font-semibold tabular-nums" />
              </button>
            </li>
          ))}
        </ul>
      </div>
      {caption && (
        <figcaption data-role="caption" className="text-body-xs text-ink-soft">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}
